import {
  Box,
  Stack,
  Grid,
  SvgIcon,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  TablePagination,
  debounce,
} from '@mui/material';
import { Formik, useFormikContext } from 'formik';
import {
  AppFormikTextField,
  AppFormikSelect,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as None } from 'assets/images/ic_none.svg';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { Toast, Utils } from 'shared/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { IRiskRegister } from 'modules/risk-management/risk-register/List';
import { IIncident } from 'modules/incidents/incidents-log/components/IncidentsCard';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import DialogBulkUpload from 'modules/ai-inventory/DialogBulkUpload';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import useSize from 'shared/hooks/useSize';

export interface IVulenerability {
  _id: string;
  title: string;
  description: string;
  business_impact: string;
  vulnerability_category: string;
  risk_rating: string;
  severity_score: any;
  residual_risk: string;
  exploitability_details: string;
  compliance_notes: string;
  confidentiality_classification: string;
  lifecycle_stage_impacted: string;
  environment: string;
  status: string;
  target_remediation_date: any;
  verification_evidence: string;
  reporter_source: string;
  owner_assignee: string;
  deployment_endpoints: any[];
  organizationId: any;
  isActive: boolean;
  createdBy: any;
  updatedBy: any;
  deletedBy: any;
  deletedAt: any;
  createdAt: string;
  updatedAt: string;
  aisystemId: IAISystem[];
  datasetId: IDataset[];
  incidentId: IIncident[];
  riskId: IRiskRegister[];
  __v: number;
}
export const SearchFormikContext = ({ onChange }: any): any => {
  const { values } = useFormikContext();

  useEffect(() => {
    onChange(values);
  }, [values]);

  return null;
};

const VulnerabilitesCard: React.FC<VulnerabilitiesProps> = () => {
  const { isMobile } = useSize();
  const dispatch = useDispatch();
  const vulnerabilitiesPagination = useSelector(
    (state: IAppState) => state.paginationState.vulnerabilities
  );

  const navigation = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [vulnaribilitiesList, setVulnaribilitiesList] = useState<
    IVulenerability[]
  >([]);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const [deleteRecord, setDeleteRecord] = useState<IVulenerability>();
  const [selectedIds, setSelectedIds] = useState<any>(new Set());
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const [openBulk, setOpenBulk] = useState(false);

  const handleClick = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  // API Calls
  const loadVulnerabilities = (): void => {
    setIsLoading(true);
    const queryString = new URLSearchParams({
      offset: String(vulnerabilitiesPagination.offset),
      limit: String(vulnerabilitiesPagination.pageLimit),
      title: vulnerabilitiesPagination.search,
      risk: vulnerabilitiesPagination.risk,
      status: vulnerabilitiesPagination.status,
    }).toString();

    VulnerabilityService.getAll(queryString)
      .then((response) => {
        const { vulnerabilities, pagination } = response.data.data;
        setVulnaribilitiesList(vulnerabilities);

        dispatch(
          PaginationActions.setPagination({
            vulnerabilities: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteRecordAPI = (id: string, callback: any): void => {
    VulnerabilityService.deleteRecord(id)
      .then(() => {
        callback(true);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnDelete = (record: IVulenerability): void => {
    setDeleteRecord(record);
    deleteRecordAPI(record._id, (status: boolean) => {
      setDeleteRecord(undefined);
      if (status) {
        Toast.showSuccessMessage(
          'The vulnerability has been successfully deleted.'
        );
        loadVulnerabilities();
      }
    });
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          vulnerabilities: {
            ...vulnerabilitiesPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [vulnerabilitiesPagination]
  );

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        vulnerabilities: {
          ...vulnerabilitiesPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleRiskChange = (riskValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        vulnerabilities: {
          ...vulnerabilitiesPagination,
          offset: 1,
          risk: riskValue,
        },
      })
    );
  };

  const handleSelectAll = (checked: boolean): void => {
    setSelectedIds(
      checked ? new Set(vulnaribilitiesList.map((v: any) => v._id)) : new Set()
    );
  };

  const handleDeselectAll = (): void => {
    setSelectedIds(new Set());
  };

  const updateBulkStatus = (status: string): void => {
    if (selectedIds.size === 0) return;
    VulnerabilityService.bulkUpdateStatus({
      ids: Array.from(selectedIds),
      status,
    })
      .then(() => {
        handleClose();
        handleDeselectAll();
        loadVulnerabilities();
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      });
  };

  const handleRowCheck = (id: string, checked: boolean): void => {
    setSelectedIds((prev: any) => {
      const dataIds = new Set(prev);
      if (checked) {
        dataIds.add(id);
      } else {
        dataIds.delete(id);
      }
      return dataIds;
    });
  };

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        vulnerabilities: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        vulnerabilities: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        vulnerabilities: {
          offset: 1,
          search: '',
          risk: '',
          status: '',
        },
      })
    );
  };

  // Hooks
  useEffect(() => {
    loadVulnerabilities();
  }, [
    vulnerabilitiesPagination.offset,
    vulnerabilitiesPagination.pageLimit,
    vulnerabilitiesPagination.search,
    vulnerabilitiesPagination.status,
    vulnerabilitiesPagination.risk,
  ]);

  // UI Elements
  return (
    <Box className="vulnaribilities-content">
      <Stack direction="column" spacing={5}>
        <Formik
          initialValues={{
            'search-programs': vulnerabilitiesPagination.search,
            status: vulnerabilitiesPagination.status,
            risk_rating: vulnerabilitiesPagination.risk,
          }}
          enableReinitialize
          onSubmit={() => console.log('searchprograms')}
        >
          {({ resetForm }) => (
            <>
              {/* <SearchFormikContext onChange={debouncSearch} /> */}
              <Grid
                container
                className="incidents-content-nav"
                spacing={2}
                alignItems="flex-start"
              >
                <Grid item xs={12} sm={4} md={5} lg={5}>
                  <Box className="vulnerability-search" width="100%">
                    <AppFormikTextField
                      inputProps={{
                        startAdornment: (
                          <SvgIcon
                            component={Search}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        ),
                      }}
                      outerLabel={false}
                      placeholder="Search vulnerabilities.."
                      name="search-programs"
                      onChange={(e: any) => debouncSearch(e.target.value)}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <AppFormikSelect
                    name="status"
                    className={`rework-selection ${!isMobile ? 'mt-2' : ''}`}
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    placeholder="All Statuses"
                    notEmphasized
                    notSelectablePlaceholder={false}
                    items={[
                      'Detected',
                      'Investigating',
                      'Triaged',
                      'Assessed',
                      'Notified',
                      'Analyzed',
                      'Contained',
                      'Corrected',
                      'Remediated',
                      'Closed',
                    ]}
                    onChange={(e: any) => handleStatusChange(e.target.value)}
                    sx={{ mt: { xs: 0, sm: 5 } }}
                  />
                </Grid>

                {/* RISK */}
                <Grid item xs={12} sm={2} md={2} lg={2}>
                  <AppFormikSelect
                    name="risk_rating"
                    className={`rework-selection ${!isMobile ? 'mt-2' : ''}`}
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    placeholder="All Risk"
                    notEmphasized
                    notSelectablePlaceholder={false}
                    items={['Critical', 'High', 'Medium', 'Low']}
                    onChange={(e: any) => handleRiskChange(e.target.value)}
                  />
                </Grid>

                {/* CLEAR FILTERS */}
                <Grid item xs={12} sm={3} md={2} lg={2}>
                  <AppPrimaryButtonOutlined
                    fullWidth
                    title="Clear Filters"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      resetForm();
                      handleClearFilter();
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Formik>
        <Card variant="outlined">
          <CardHeader
            title="Vulnerabilities"
            action={
              <Box
                sx={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                {orgACLConfig.vulnerabilities?.includes(
                  'vulnerabilities.aiVulnerabilities.bulkUpload'
                ) && (
                  <AppPrimaryButtonOutlined
                    title="Bulk Upload"
                    startIcon={
                      <SvgIcon
                        component={Upload}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        // htmlColor="#76A81F"
                      />
                    }
                    onClick={() => {
                      setOpenBulk(true);
                    }}
                  />
                )}
                {orgACLConfig.vulnerabilities?.includes(
                  'vulnerabilities.aiVulnerabilities.bulkActions'
                ) && (
                  <AppPrimaryButtonOutlined
                    id="basic-button"
                    title="Bulk actions"
                    onClick={handleClick}
                  />
                )}
                {orgACLConfig.vulnerabilities?.includes(
                  'vulnerabilities.aiVulnerabilities.addNewVulnerability'
                ) && (
                  <AppPrimaryButton
                    className="btn-small"
                    onClick={() => navigation('/vulnerabilities/create')}
                    startIcon={
                      <SvgIcon
                        component={Plus}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                      />
                    }
                    title="New Vulnerability"
                  />
                )}
              </Box>
            }
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem
              onClick={() => updateBulkStatus('Triaged')}
              disabled={selectedIds.size === 0}
            >
              Mark as triaged
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('Categorized')}
              disabled={selectedIds.size === 0}
            >
              Mark as categorized
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('RemediationPlanned')}
              disabled={selectedIds.size === 0}
            >
              Mark as remediation planned
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('InRemediation')}
              disabled={selectedIds.size === 0}
            >
              Mark as in remediation
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('Mitigated')}
              disabled={selectedIds.size === 0}
            >
              Mark as mitigated
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('Verified')}
              disabled={selectedIds.size === 0}
            >
              Mark as verified
            </MenuItem>
            <MenuItem
              onClick={() => updateBulkStatus('RiskAccepted')}
              disabled={selectedIds.size === 0}
            >
              Mark as risk accepted
            </MenuItem>
          </Menu>
          <CardContent>
            {isLoading && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}
            {!isLoading && vulnaribilitiesList.length === 0 && (
              <Stack direction="column" spacing={2} justifyContent="center">
                <EmptyPlaceholder
                  icon={
                    <SvgIcon
                      component={None}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  title="No vulnerabilities found"
                  message="Get started by creating a new vulnerability record."
                />
                <Box className="vulnerabilites-button">
                  {orgACLConfig.vulnerabilities?.includes(
                    'vulnerabilities.aiVulnerabilities.addNewVulnerability'
                  ) && (
                    <AppPrimaryButton
                      title="Create Vulnerability"
                      startIcon={
                        <SvgIcon
                          component={Plus}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#76A81F"
                        />
                      }
                      onClick={() => {
                        navigation('/vulnerabilities/create');
                      }}
                    />
                  )}
                </Box>
              </Stack>
            )}
            {!isLoading && vulnaribilitiesList.length > 0 && (
              <TableContainer
                className="app-table"
                sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={
                            vulnaribilitiesList.length > 0 &&
                            vulnaribilitiesList.every((v: any) =>
                              selectedIds.has(v._id)
                            )
                          }
                          indeterminate={
                            vulnaribilitiesList.some((v: any) =>
                              selectedIds.has(v._id)
                            ) &&
                            !vulnaribilitiesList.every((v: any) =>
                              selectedIds.has(v._id)
                            )
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{
                            '&.Mui-checked': {
                              color: '#d1f27a',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Risk</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Target Date</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vulnaribilitiesList.length > 0 ? (
                      vulnaribilitiesList.map((vulnerable: any) => (
                        <TableRow>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(vulnerable._id)}
                              onChange={(e) =>
                                handleRowCheck(vulnerable._id, e.target.checked)
                              }
                            />
                          </TableCell>
                          <TableCell>VUL-{vulnerable._id.slice(-8)}</TableCell>
                          <TableCell className="word-break">
                            {vulnerable.title}
                          </TableCell>
                          <TableCell>
                            <Chip label={vulnerable.status} />
                          </TableCell>
                          <TableCell>
                            <Chip label={vulnerable.risk_rating} />
                          </TableCell>
                          <TableCell>
                            {vulnerable.vulnerability_category}
                          </TableCell>
                          <TableCell className="word-break">
                            {vulnerable.owner_assignee}
                          </TableCell>
                          <TableCell>
                            {vulnerable.target_remediation_date
                              ? Utils.formatDate(
                                  vulnerable.target_remediation_date
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {Utils.formatDate(vulnerable.updatedAt)}
                          </TableCell>
                          <TableCell>
                            <Stack
                              spacing={2}
                              direction="row"
                              alignItems="center"
                            >
                              {orgACLConfig.vulnerabilities?.includes(
                                'vulnerabilities.aiVulnerabilities.viewVulnerability'
                              ) && (
                                <AppPrimaryButtonOutlined
                                  title="View"
                                  onClick={() => {
                                    navigation(
                                      `/vulnerabilities/${vulnerable._id}`
                                    );
                                  }}
                                  className="primary-light-fill btn-extra-small"
                                />
                              )}
                              {orgACLConfig.vulnerabilities?.includes(
                                'vulnerabilities.aiVulnerabilities.deleteVulnerability'
                              ) && (
                                <IconButton
                                  onClick={() => {
                                    actionOnDelete(vulnerable);
                                  }}
                                >
                                  {deleteRecord?._id === vulnerable._id && (
                                    <CircularProgress />
                                  )}
                                  {deleteRecord?._id !== vulnerable._id && (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <EmptyPlaceholder title="No vulnerability found matching your criteria." />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {!isLoading && vulnaribilitiesList.length > 0 && (
          <TablePagination
            component="div"
            count={vulnerabilitiesPagination.totalRecords}
            page={vulnerabilitiesPagination.offset - 1}
            rowsPerPage={vulnerabilitiesPagination.pageLimit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            disabled={isLoading}
          />
        )}
      </Stack>
      {openBulk && (
        <DialogBulkUpload
          openBulk={openBulk}
          onClose={(reload: boolean) => {
            if (reload) {
              loadVulnerabilities();
            }
            setOpenBulk(false);
          }}
          title="Bulk Upload Vulnerabilities"
          variant="vulnerabilities"
        />
      )}
    </Box>
  );
};

export default VulnerabilitesCard;
